import { inject, Injectable } from '@angular/core';
import { combineLatest, map, Observable } from 'rxjs';
import { selectAllLoggerMeasurements } from '@app/main/main-screen/store/data/logger-measurements/logger-measurements.selectors';
import { Store } from '@ngrx/store';
import { selectLoggerDeploymentsState } from '@app/main/main-screen/store/data/logger-deployments/logger-deployments.selectors';
import { ILdLogger } from '@app/main/leak-detection/interfaces/ld-logger';
import { distinctUntilChanged, shareReplay } from 'rxjs/operators';
import { ILoggerDeployment } from '@app/api/interfaces/logger-deployment-data.interface';
import { ILoggerMeasurement } from '@app/api/interfaces/logger-measurement.interface';

@Injectable({ providedIn: 'root' })
export class LdDataAggregationService {
  private readonly store = inject(Store);

  // the deployments' ids and entities from one state: selected apart, a deployments load changes both and runs the
  // whole LD chain twice, the first time with the new list and the old entities
  public readonly ldLoggers$: Observable<ILdLogger[]> = combineLatest([
    this.store.select(selectAllLoggerMeasurements),
    this.store
      .select(selectLoggerDeploymentsState)
      .pipe(distinctUntilChanged((a, b) => a.ids === b.ids && a.entities === b.entities)),
  ]).pipe(
    map(([loggerMeasurements, { ids, entities: loggerDeploymentsMap }]) => {
      const deploymentIds = ids as string[];

      if (!deploymentIds.length) return [];

      const usedDeploymentIds = new Set<string>();

      const ldLoggers: ILdLogger[] = loggerMeasurements
        .filter(loggerMeasurement => loggerDeploymentsMap[loggerMeasurement.deploymentId])
        .map(loggerMeasurement => {
          usedDeploymentIds.add(loggerMeasurement.deploymentId);

          const deployment = loggerDeploymentsMap[loggerMeasurement.deploymentId]!;

          return this.prepareLdLogger(deployment, loggerMeasurement);
        });

      return [
        ...ldLoggers,
        ...deploymentIds
          .filter(id => !usedDeploymentIds.has(id))
          .map(id => this.prepareLdLogger(loggerDeploymentsMap[id]!)),
      ];
    }),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  public prepareLdLogger(deployment: ILoggerDeployment, measurement?: ILoggerMeasurement): ILdLogger {
    return {
      id: measurement ? `${deployment.id}_${measurement.id}` : `${deployment.id}_undefined`,
      measurement,
      deployment,
    };
  }
}
